import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import { SITE_URL, SITE_NAME, SITE_LOGO } from '@/utils/seo';

export interface ArticleSection {
  id: string;
  title: string;
  type: 'text' | 'cards' | 'table' | 'tips' | 'faq' | 'cta' | 'image' | 'steps';
  content?: string;
  cards?: { title: string; desc: string; icon?: string }[];
  tableHeaders?: string[];
  tableRows?: string[][];
  tips?: { title: string; desc: string; icon?: string }[];
  faqs?: { q: string; a: string }[];
  steps?: { step: string; title: string; desc: string }[];
  imageUrl?: string;
  imageAlt?: string;
  ctaText?: string;
  ctaLink?: string;
  ctaExternal?: boolean;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  note?: string;
}

export interface ArticleData {
  slug: string;
  title: string;
  subtitle: string;
  intro: string;
  heroImgUrl: string;
  heroImgAlt: string;
  updatedAt: string;
  readTime: string;
  tags: { label: string; color: 'accent' | 'primary' | 'secondary' }[];
  sections: ArticleSection[];
  relatedLinks: { label: string; to: string }[];
}

const tagColorMap = {
  accent: 'text-accent-600 bg-accent-100',
  primary: 'text-primary-600 bg-primary-100',
  secondary: 'text-secondary-600 bg-secondary-100',
};

function stripHtml(html: string): string {
  if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, '');
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

function buildJsonLd(data: ArticleData): object {
  const articleUrl = `${SITE_URL}/${data.slug}`;
  const plainIntro = stripHtml(data.intro);
  const graph: object[] = [];

  // Article schema
  graph.push({
    '@type': 'Article',
    headline: data.title,
    description: plainIntro.substring(0, 160),
    image: data.heroImgUrl,
    url: articleUrl,
    datePublished: '2026-07-01',
    dateModified: '2026-07-15',
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: SITE_LOGO,
      },
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: SITE_LOGO,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    inLanguage: 'de-DE',
  });

  // BreadcrumbList schema
  graph.push({
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Startseite',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: data.title,
        item: articleUrl,
      },
    ],
  });

  // FAQPage schema (only if there are FAQ sections)
  const faqSections = data.sections.filter((s) => s.type === 'faq');
  if (faqSections.length > 0) {
    const allFaqs: { q: string; a: string }[] = [];
    faqSections.forEach((s) => {
      if (s.faqs) allFaqs.push(...s.faqs);
    });

    if (allFaqs.length > 0) {
      graph.push({
        '@type': 'FAQPage',
        mainEntity: allFaqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a,
          },
        })),
      });
    }
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

export default function ArticleLayout({ data }: { data: ArticleData }) {
  const jsonLd = buildJsonLd(data);

  const tocItems = data.sections
    .filter((s) => s.type !== 'cta' && s.type !== 'image')
    .map((s) => s.title);

  function renderSection(section: ArticleSection, idx: number) {
    switch (section.type) {
      case 'text':
        return (
          <section id={section.id} className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              {section.title}
            </h2>
            {section.content && (
              <div
                className="text-sm md:text-base text-foreground-700 leading-relaxed prose-content"
                dangerouslySetInnerHTML={{ __html: section.content }}
              />
            )}
            {section.imageUrl && (
              <div className="w-full h-48 md:h-64 rounded-xl overflow-hidden mt-4">
                <img
                  src={section.imageUrl}
                  alt={section.imageAlt || section.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </section>
        );

      case 'cards':
        return (
          <section id={section.id} className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              {section.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {section.cards?.map((card, cIdx) => (
                <div key={cIdx} className="flex items-start gap-3 p-5 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${card.icon || 'ri-check-line'} text-accent-600`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{card.title}</h3>
                    <p className="text-xs text-foreground-600 mt-0.5">{card.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );

      case 'table':
        return (
          <section id={section.id} className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              {section.title}
            </h2>
            {section.content && (
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">{section.content}</p>
            )}
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-secondary-100">
                    {section.tableHeaders?.map((h, hIdx) => (
                      <th
                        key={hIdx}
                        className={`px-4 py-3 font-semibold text-foreground-900 ${hIdx === 0 ? 'text-left rounded-tl-lg' : 'text-center'} ${hIdx === (section.tableHeaders?.length || 1) - 1 ? 'rounded-tr-lg' : ''}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.tableRows?.map((row, rIdx) => (
                    <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}>
                      {row.map((cell, cIdx) => (
                        <td
                          key={cIdx}
                          className={`px-4 py-3 ${cIdx === 0 ? 'font-medium text-foreground-800' : 'text-center text-accent-500'}`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {section.note && (
              <p className="text-xs text-foreground-500 mt-3">{section.note}</p>
            )}
          </section>
        );

      case 'tips':
        return (
          <section id={section.id} className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              {section.title}
            </h2>
            {section.content && (
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">{section.content}</p>
            )}
            <div className="flex flex-col gap-3 mt-4">
              {section.tips?.map((tip, tIdx) => (
                <div key={tIdx} className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${tip.icon || 'ri-shield-check-line'} text-primary-600`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{tip.title}</h3>
                    <p className="text-xs text-foreground-600 mt-0.5">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            {section.note && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mt-4">
                <p className="text-xs text-foreground-700 flex items-start gap-2">
                  <i className="ri-error-warning-line text-yellow-600 mt-0.5"></i>
                  <span dangerouslySetInnerHTML={{ __html: section.note }} />
                </p>
              </div>
            )}
          </section>
        );

      case 'faq':
        return (
          <section id={section.id} className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              {section.title}
            </h2>
            <div className="flex flex-col gap-3 mt-4">
              {section.faqs?.map((faq, fIdx) => (
                <div key={fIdx} className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">{faq.q}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>
        );

      case 'image':
        return section.imageUrl ? (
          <section id={section.id} className="mb-12">
            <div className="w-full h-48 md:h-72 rounded-2xl overflow-hidden">
              <img
                src={section.imageUrl}
                alt={section.imageAlt || section.title}
                className="w-full h-full object-cover"
              />
            </div>
          </section>
        ) : null;

      case 'steps':
        return (
          <section id={section.id} className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              {section.title}
            </h2>
            {section.content && (
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">{section.content}</p>
            )}
            <div className="flex flex-col gap-4 mt-4">
              {section.steps?.map((item, sIdx) => (
                <div key={sIdx} className="flex items-start gap-4 p-5 bg-background-100 rounded-xl border border-background-200/70">
                  <span className="text-lg font-bold text-accent-500 flex-shrink-0">{item.step}</span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                    <p className="text-sm text-foreground-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );

      case 'cta':
        return (
          <section className="mb-12">
            <div className="bg-primary-500 rounded-2xl p-8 text-center">
              <h3 className="text-xl md:text-2xl font-heading font-semibold text-background-50">
                {section.title}
              </h3>
              {section.content && (
                <p className="mt-2 text-sm text-background-50/80 max-w-lg mx-auto">{section.content}</p>
              )}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-5">
                <a
                  href={section.ctaExternal ? section.ctaLink : undefined}
                  target={section.ctaExternal ? '_blank' : undefined}
                  rel={section.ctaExternal ? 'noopener nofollow' : undefined}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-primary-700 font-semibold text-sm hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-rocket-line"></i>
                  {section.ctaText}
                </a>
                {section.secondaryCtaText && (
                  <Link
                    to={section.secondaryCtaLink || '/'}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-600 text-background-50 font-semibold text-sm hover:bg-primary-700 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-home-line"></i>
                    {section.secondaryCtaText}
                  </Link>
                )}
              </div>
            </div>
          </section>
        );

      default:
        return null;
    }
  }

  return (
    <main className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      <article className="pt-20 pb-14">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-500 transition-colors whitespace-nowrap">
              Startseite
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-800 font-medium whitespace-nowrap">{data.title}</span>
          </nav>

          {/* Article Header */}
          <header className="mb-10">
            <div className="flex flex-wrap gap-2 mb-4">
              {data.tags.map((tag, idx) => (
                <span key={idx} className={`text-xs font-semibold px-3 py-1 rounded-full ${tagColorMap[tag.color]}`}>
                  {tag.label}
                </span>
              ))}
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-900 leading-tight">
              {data.title}
            </h1>
            {data.subtitle && (
              <p className="mt-2 text-lg md:text-xl text-foreground-600 font-heading italic">{data.subtitle}</p>
            )}
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              {data.intro}
            </p>
            <div className="flex items-center gap-4 mt-6 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                Aktualisiert: {data.updatedAt}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                Lesezeit: {data.readTime}
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="w-full h-64 md:h-96 rounded-2xl overflow-hidden mb-12">
            <img
              src={data.heroImgUrl}
              alt={data.heroImgAlt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Table of Contents */}
          <nav className="bg-background-100 rounded-xl border border-background-200/70 p-6 mb-10">
            <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-4">Inhaltsverzeichnis</h2>
            <ol className="flex flex-col gap-2 text-sm">
              {tocItems.map((title, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-accent-500 font-semibold flex-shrink-0">{idx + 1}.</span>
                  <a href={`#section-${idx + 1}`} className="text-foreground-700 hover:text-accent-500 transition-colors">
                    {title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Sections */}
          {data.sections.map((section, idx) => renderSection(section, idx))}

          {/* CTA Bottom */}
          <div className="bg-primary-500 rounded-2xl p-8 text-center">
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-background-50">
              Starte jetzt mit dem OOPBuy Spreadsheet
            </h3>
            <p className="mt-2 text-sm text-background-50/80 max-w-lg mx-auto">
              Entdecke tausende geprüfte Produkte, QC-Bilder und deutsche Einkaufsanleitungen – alles an einem Ort.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-5">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-primary-700 font-semibold text-sm hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-rocket-line"></i>
                OOPBuy Spreadsheet entdecken
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-600 text-background-50 font-semibold text-sm hover:bg-primary-700 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line"></i>
                Zurück zur Startseite
              </Link>
            </div>
          </div>

          {/* Related Links */}
          <div className="mt-12 pt-8 border-t border-background-200/70">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">Verwandte Inhalte</h3>
            <div className="flex flex-wrap gap-2">
              {data.relatedLinks.map((link, idx) => (
                <Link
                  key={idx}
                  to={link.to}
                  className="text-xs bg-background-100 hover:bg-background-200 px-3 py-1.5 rounded-full text-foreground-700 transition-colors whitespace-nowrap"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}